import { useQuery } from '@tanstack/react-query'
import type { UseQueryResult } from '@tanstack/react-query'

import { queryKeys } from '../../../../config/queryKeys'
import { getPlayerProgress } from '../../../../services/player/playerProgress'
import { ALL_SPORTS } from '../constants'
import type { PlayerProgress, ProgressRange, StatsRole } from '../types'
import { sameProgressSeries } from '../utils/progressKey'

export interface UsePlayerProgressArgs {
  playerId?: string
  sportId: string | null
  /** A specific generic sport under `sportId` (the base Generic sport). */
  genericSportId?: string
  role: StatsRole
  range: ProgressRange
  timezone: string
  /** ANDed into the query's own gate (e.g. wait for the role to resolve). */
  enabled?: boolean
}

/**
 * Bucketed progress series for the Progress card. All Sports sends neither
 * sport nor role. The previous chart stays on screen while a new range (or
 * timezone) loads, but never across a player, sport, generic sport or role
 * switch.
 */
export const usePlayerProgress = ({
  playerId,
  sportId,
  genericSportId,
  role,
  range,
  timezone,
  enabled = true,
}: UsePlayerProgressArgs): UseQueryResult<PlayerProgress, Error> => {
  'use memo'

  const isAllSports = sportId === ALL_SPORTS
  const querySportId = isAllSports ? undefined : sportId ?? undefined
  const queryRole = isAllSports ? undefined : role
  const queryGenericSportId = isAllSports ? undefined : genericSportId

  const queryKey: readonly unknown[] = queryKeys.playerStats.progress(
    playerId,
    sportId,
    queryGenericSportId,
    queryRole,
    range,
    timezone
  )

  return useQuery({
    queryKey,
    queryFn: () =>
      getPlayerProgress({
        playerId: playerId as string,
        sportId: querySportId,
        genericSportId: queryGenericSportId,
        role: queryRole,
        range,
        timezone,
      }),
    enabled: enabled && Boolean(playerId) && Boolean(sportId),
    placeholderData: (previousData, previousQuery) =>
      previousQuery && sameProgressSeries(previousQuery.queryKey, queryKey)
        ? previousData
        : undefined,
  })
}

export default usePlayerProgress
